import React, { useContext, useEffect } from 'react';
import { PublishingView, PublishingViewProps } from './Publishing.view';
import { RegisterOptions, useForm, UseFormReturn } from 'react-hook-form';
import { FormElementAction } from '../../../models/basic-objects';
import {
  FbLinkPreview,
  GetWorkspaceLazyQueryHookResult,
  ImportMediaBehavior,
  MinimumRecycleTime,
  PostMultiMediaAttachMode,
  PostQueuePosition,
  UpdateWorkspaceSettingsInput,
  UpdateWorkspaceSettingsMutationFn
} from '../../../models/graphql';
import { ApolloError } from '@apollo/client';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { FormOptions } from '../../../constants/form';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';
import { AppContext } from '../../../providers/AppProvider';

export type PublishingControllerProps = Omit<
  PublishingViewProps,
  | 'attributionInstagramToggleAction'
  | 'evergreenSelectAction'
  | 'fbLinkPreviewSelectAction'
  | 'importMediaBehaviorSelectAction'
  | 'isLoading'
  | 'minRecycleTimeSelectAction'
  | 'multiMediaAttachModeSelectAction'
  | 'queuePositionSelectAction'
  | 'register'
  | 'rules'
  | 'toggleRules'
  | 'useFormInstance'
> & {
  getWorkspace: GetWorkspaceLazyQueryHookResult[0];
  updateWorkspaceSettings: UpdateWorkspaceSettingsMutationFn;
};

export const PublishingController = (props: PublishingControllerProps): JSX.Element => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const rules: RegisterOptions = {};
  const toggleRules: RegisterOptions = {};

  const useFormInstance: UseFormReturn = useForm(FormOptions);

  // Get workspace settings each time the workspaceID is changed
  useEffect(() => {
    selectedWorkspaceId &&
      props.getWorkspace({
        variables: { workspaceId: selectedWorkspaceId }
      });
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [selectedWorkspaceId]);

  const evergreenSelectAction: FormElementAction<boolean> = (value: boolean, complete) => {
    const currentValue = props.workspaceSettingsResults.data?.workspace?.settings.evergreenRecycling;

    if (selectedWorkspaceId && currentValue !== value) {
      const input: UpdateWorkspaceSettingsInput = {
        evergreenRecycling: `${value}` === 'true'
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const minRecycleTimeSelectAction: FormElementAction<MinimumRecycleTime> = (value, complete) => {
    const currentValue = props.workspaceSettingsResults.data?.workspace?.settings.minimumRecycleTime;

    if (selectedWorkspaceId && currentValue !== value) {
      const input: UpdateWorkspaceSettingsInput = {
        minimumRecycleTime: value
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const multiMediaAttachModeSelectAction: FormElementAction<PostMultiMediaAttachMode> = (value, complete) => {
    const currentValue = props.workspaceSettingsResults.data?.workspace?.settings.multiMediaAttachMode;

    if (selectedWorkspaceId && currentValue !== value) {
      const input: UpdateWorkspaceSettingsInput = {
        multiMediaAttachMode: value
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const queuePositionSelectAction: FormElementAction<PostQueuePosition> = (value, complete) => {
    const currentValue = props.workspaceSettingsResults.data?.workspace?.settings.defaultQueuePosition;

    if (selectedWorkspaceId && currentValue !== value) {
      const input: UpdateWorkspaceSettingsInput = {
        defaultQueuePosition: value
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const fbLinkPreviewSelectAction: FormElementAction<FbLinkPreview> = (value, complete) => {
    const currentValue = props.workspaceSettingsResults.data?.workspace?.settings.fbLinkPreview;

    if (selectedWorkspaceId && currentValue !== value) {
      const input: UpdateWorkspaceSettingsInput = {
        fbLinkPreview: value
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const importMediaBehaviorSelectAction: FormElementAction<ImportMediaBehavior> = (value, complete) => {
    const currentValue = props.workspaceSettingsResults.data?.workspace?.settings.importMediaBehavior;

    if (selectedWorkspaceId && currentValue !== value) {
      const input: UpdateWorkspaceSettingsInput = {
        importMediaBehavior: value
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const attributionInstagramToggleAction: ToggleAction = (value, complete) => {
    if (selectedWorkspaceId) {
      const input: UpdateWorkspaceSettingsInput = {
        attributionInstagram: value
      };

      props
        .updateWorkspaceSettings({
          variables: { input: input, workspaceId: selectedWorkspaceId }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your settings',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const isLoading: boolean = props.workspaceSettingsResults.loading || !props.workspaceSettingsResults.called;

  const viewProps: PublishingViewProps = {
    ...props,
    useFormInstance,
    rules,
    toggleRules,
    evergreenSelectAction,
    minRecycleTimeSelectAction,
    multiMediaAttachModeSelectAction,
    attributionInstagramToggleAction,
    queuePositionSelectAction,
    fbLinkPreviewSelectAction,
    importMediaBehaviorSelectAction,
    isLoading
  };
  return <PublishingView {...viewProps} />;
};
